import type { Dayjs } from "dayjs";
import type {
  FilterValue,
  SorterResult,
  TableCurrentDataSource,
} from "antd/es/table/interface";
import type { Alert, ALERT_STATUS, ALERT_TYPE, Device, TrapSite } from "../../types";
import type { AlertSorter } from "../../types/api/request/alert";
import { getAlertTimestamp } from "../../utils/helpers";

export const TYPE_LABEL: Record<ALERT_TYPE, string> = {
  maintenance: "Alert",
  notification: "Notification",
  detection: "Detection",
};

export interface AlertTableRecord {
  key: string;
  alertId: string;
  type: ALERT_TYPE;
  deviceName: string;
  /** Only in the superuser panel's All view, where alerts span organizations. */
  org?: string;
  /** As `org`, but the display name. */
  orgName?: string;
  trapsiteName: string;
  trapsiteAddress: string;
  message: string;
  description: string;
  createdAt: Date;
  status: ALERT_STATUS;
}

export interface AlertFilters {
  dateRange: [Dayjs | null, Dayjs | null] | null;
  types: ALERT_TYPE[];
  deviceIds: string[];
  messages: string[];
  statuses: ALERT_STATUS[];
}

export const INITIAL_FILTERS: AlertFilters = {
  dateRange: null,
  types: [],
  deviceIds: [],
  messages: [],
  statuses: [],
};

export const deviceNameMap = (
  devices: Partial<Device>[] | undefined,
): Map<string, string> => {
  if (!devices) return new Map<string, string>();
  return new Map(devices.map((d) => [d.uuid ?? "", d.display_name ?? ""]));
};

export interface DeviceOrg {
  org: string;
  name: string;
}

/** Device uuid to its organization and that organization's display name, for the All view. */
export const deviceOrgMap = (
  devices: Partial<Device>[] | undefined,
  orgNames: Map<string, string>,
): Map<string, DeviceOrg> => {
  if (!devices) return new Map<string, DeviceOrg>();
  return new Map(
    devices.map((d) => [
      d.uuid ?? "",
      { org: d.org ?? "", name: orgNames.get(d.org ?? "") || d.org || "" },
    ]),
  );
};

export const deviceTrapSiteMap = (
  devices: Partial<Device>[] | undefined,
  trapSites: Partial<TrapSite>[] | undefined,
): Map<string, Partial<TrapSite> | undefined> => {
  if (!devices || !trapSites) return new Map();
  const trapSiteById = new Map(trapSites.map((t) => [t._id, t]));
  return new Map(
    devices
      .filter((d) => d.uuid && d.trap_site_id)
      .map((d) => [d.uuid as string, trapSiteById.get(d.trap_site_id as string)]),
  );
};

export const toAlertTableRows = (
  alerts: Alert[] | undefined,
  nameByUuid: Map<string, string>,
  trapSiteByUuid: Map<string, Partial<TrapSite> | undefined>,
  orgByUuid?: Map<string, DeviceOrg>,
): AlertTableRecord[] => {
  if (!alerts) return [];

  return alerts.map((alert) => {
    const deviceUuid = alert.context?.device ?? "";
    const deviceName = nameByUuid.get(deviceUuid) ?? "";
    const trapSite = trapSiteByUuid.get(deviceUuid);

    return {
      key: alert._id,
      alertId: alert.name,
      type: alert.type,
      deviceName,
      ...(orgByUuid && {
        org: orgByUuid.get(deviceUuid)?.org ?? "",
        orgName: orgByUuid.get(deviceUuid)?.name ?? "",
      }),
      trapsiteName: trapSite?.display_name ?? "",
      trapsiteAddress: trapSite?.address ?? "",
      message: alert.message,
      description: alert.description,
      createdAt: getAlertTimestamp(alert),
      status: alert.status,
    };
  });
};

/**
 * `orgByUuid` suffixes each trap with its organization, for the All view:
 * trap names are only unique within one, so two "Trap 1"s would otherwise be
 * indistinguishable in the list.
 */
export const deviceFilterOptions = (
  devices: Partial<Device>[] | undefined,
  orgByUuid?: Map<string, DeviceOrg>,
): { text: string; value: string }[] => {
  if (!devices) return [];
  return devices.map((d) => {
    const name = d.display_name ?? d.uuid ?? "";
    const orgName = orgByUuid?.get(d.uuid ?? "")?.name;
    return {
      text: orgName ? `${name} · ${orgName}` : name,
      value: d.uuid ?? "",
    };
  });
};

export const toAlertFilters = (
  columnFilters: Record<string, FilterValue | null>,
): Pick<AlertFilters, "types" | "deviceIds" | "messages" | "statuses"> => ({
  types: (columnFilters.type ?? []) as ALERT_TYPE[],
  deviceIds: (columnFilters.deviceName ?? []) as string[],
  messages: (columnFilters.description ?? []) as string[],
  statuses: (columnFilters.status ?? []) as ALERT_STATUS[],
});

export const toAlertSorter = (
  sorterResult: SorterResult<AlertTableRecord> | SorterResult<AlertTableRecord>[],
): AlertSorter => {
  const s = Array.isArray(sorterResult) ? sorterResult[0] : sorterResult;
  return s.order
    ? {
        [s.field === "deviceName" ? "device" : (s.field as string)]:
          s.order === "ascend" ? 1 : -1,
      }
    : {};
};

export const pageAfterChange = (
  action: TableCurrentDataSource["action"],
  current: number | undefined,
): number => (action === "paginate" ? current ?? 1 : 1);
